import React, { useMemo, useState } from "react";
import {
  Paper, Box, TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, IconButton, Tooltip, Chip, TablePagination, CircularProgress,
  Typography, Button, Switch, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import GetApp from "@mui/icons-material/GetApp";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";

const Thumb = ({ b }) => (
  <Box
    sx={{
      width: 180,
      height: 80,
      borderRadius: 1,
      border: "1px solid",
      borderColor: "divider",
      position: "relative",
      overflow: "hidden",
      bgcolor: b?.background || "background.default",
    }}
  >
    {b?.imageSrc && (
      <Box
        component="img"
        alt={b?.imageAlt || ""}
        src={b.imageSrc}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: b?.tipo === "card" ? b?.fit || "cover" : "contain",
          objectPosition: b?.tipo === "card" ? "center" : "right",
          opacity: 0.95,
        }}
      />
    )}
    {b?.tipo === "carousel" && (
      <Box sx={{ position: "absolute", left: 8, top: 8, right: 8, color: "white", zIndex: 2 }}>
        {b?.eyebrow && (
          <Box
            sx={{
              display: "inline-flex",
              px: 1,
              py: 0.25,
              fontSize: 10,
              borderRadius: 999,
              bgcolor: "rgba(0,0,0,.35)",
            }}
          >
            {b.eyebrow}
          </Box>
        )}
        {b?.title && (
          <Typography variant="caption" sx={{ display: "block", lineHeight: 1.1 }}>
            <b>{b.title}</b>
          </Typography>
        )}
      </Box>
    )}
  </Box>
);

const BannerTable = ({
  banners = [],
  rowCount = 0,
  page = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  onEditBanner,
  onDeleteBanner,
  onToggleStatus,

  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
}) => {
  const { t } = useTranslation("settings");
  const [localSearch, setLocalSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  const ordered = useMemo(
    () => banners.slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    [banners]
  );

  const selectedArr = Array.from(selected);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const tipoLabel = (tipo) => {
    if (tipo === "card") return t("shared.card");
    if (tipo === "heroImage") return t("shared.simpleHero");
    return t("shared.carousel");
  };

  const exportColumns = useMemo(
    () => [
      { field: "ordem", headerName: t("shared.order") },
      { field: "nome", headerName: t("shared.nameTitle") },
      { field: "tipo", headerName: t("shared.type") },
      { field: "status", headerName: t("shared.status") },
    ],
    [t]
  );

  const selectedData = useMemo(
    () =>
      ordered
        .filter((b) => selected.has(b.id))
        .map((b) => ({
          ordem: String(b.ordem ?? ""),
          nome: b.nome || b.title || "—",
          tipo: tipoLabel(b.tipo),
          status: b.status ? t("shared.active") : t("shared.inactive"),
        })),
    [ordered, selected, t]
  );

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text(t("bannerTable.exportTitle"), 20, 10);
    const headers = exportColumns.map((c) => c.headerName);
    const body = selectedData.map((row) => exportColumns.map((col) => row[col.field]));
    doc.autoTable({ head: [headers], body });
    doc.save("banners.pdf");
  };

  const csvData = useMemo(
    () =>
      selectedData.map((row) => {
        const o = {};
        exportColumns.forEach((c) => (o[c.headerName] = row[c.field]));
        return o;
      }),
    [selectedData, exportColumns]
  );

  const allOnPageSelected = ordered.length > 0 && ordered.every((x) => selected.has(x.id));
  const isRowSelected = (id) => selected.has(id);
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const ids = ordered.map((x) => x.id);
      const next = new Set(prev);
      if (allOnPageSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });

  return (
    <Paper variant="outlined">
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder={t("shared.searchPlaceholder")}
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch?.(localSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {selectedArr.length > 0 && (
        <Box sx={{ mx: 1.5, mb: 2, py: 1, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="subtitle1" color="text.secondary">
            {t("shared.selectedCount", { count: selectedArr.length })}
          </Typography>
          <Button variant="contained" color="primary" startIcon={<GetApp />} onClick={exportPDF} sx={{ textTransform: "uppercase" }}>
            {t("shared.exportPdf")}
          </Button>
          <Button variant="contained" color="primary" startIcon={<GetApp />} sx={{ textTransform: "uppercase" }}>
            <CSVLink data={csvData} filename="banners.csv" style={{ textDecoration: "none", color: "inherit" }}>
              {t("shared.exportCsv")}
            </CSVLink>
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => setConfirmBulkDelete(true)}
            sx={{ textTransform: "uppercase" }}
          >
            {`${t("shared.deleteSelected")} (${selectedArr.length})`}
          </Button>
        </Box>
      )}

      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}>
        <DialogTitle>{t("shared.deleteSelectedTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("shared.deleteSelectedConfirm", { count: selectedArr.length })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)}>{t("shared.cancel")}</Button>
          <Button
            color="error"
            onClick={() => {
              onBulkDelete?.(selectedArr);
              setConfirmBulkDelete(false);
            }}
          >
            {t("shared.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={!allOnPageSelected && selectedArr.length > 0}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>{t("shared.order")}</TableCell>
              <TableCell>{t("bannerTable.preview")}</TableCell>
              <TableCell>{t("shared.nameTitle")}</TableCell>
              <TableCell>{t("shared.type")}</TableCell>
              <TableCell>{t("shared.status")}</TableCell>
              <TableCell align="right">{t("shared.actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3, display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">{t("shared.loading")}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : ordered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">{t("bannerTable.empty")}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              ordered.map((b) => {
                const checked = isRowSelected(b.id);
                return (
                  <TableRow key={b.id} hover selected={checked}>
                    <TableCell>
                      <Checkbox checked={checked} onChange={() => toggleSelect(b.id)} />
                    </TableCell>
                    <TableCell>{b.ordem ?? ""}</TableCell>
                    <TableCell><Thumb b={b} /></TableCell>
                    <TableCell>{b.nome || b.title || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={tipoLabel(b.tipo)} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch size="small" checked={!!b.status} onChange={() => onToggleStatus?.(b)} />
                        <Chip
                          size="small"
                          label={b.status ? t("shared.active") : t("shared.inactive")}
                          color={b.status ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("shared.edit")}>
                        <IconButton size="small" onClick={() => onEditBanner?.(b)}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("shared.delete")}>
                        <IconButton size="small" onClick={() => onDeleteBanner?.(b.id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: 1 }}>
        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage={t("shared.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("shared.of")} ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Paper>
  );
};

export default BannerTable;
